import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconSchool } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import DisplayMessage from "../shared/DisplayMessage/DisplayMessage";

function TrainingTabAllProgramsPage() {
  const { getVerifiedJWT } = useAuth();
  const navigate = useNavigate();

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        setLoading(true);
        setError(null);

        const jwt = await getVerifiedJWT();
        if (!jwt) {
          setPrograms([]);
          return;
        }

        const response = await fetch(getApiEndpoint("/v1/programs/mine"), {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          cache: "no-cache",
        });

        if (!response.ok) {
          throw new Error("Failed to fetch programs");
        }

        const result = await response.json();
        setPrograms(result?.data || []);
      } catch (err) {
        setError(err.message || "Failed to fetch programs");
        setPrograms([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPrograms();
  }, [getVerifiedJWT]);

  const safePrograms = useMemo(() => programs || [], [programs]);

  if (loading) {
    return (
      <div className="training-page-no-courses-message">Loading programs…</div>
    );
  }

  if (error) {
    return (
      <DisplayMessage
        type="error"
        message={`Error loading programs: ${error}`}
      />
    );
  }

  return (
    <div className="training-page-all-courses-view">
      <div className="training-page-all-videos-header">
        <div>
          <h2 className="training-page-all-videos-title">All Programs</h2>
          <p className="training-page-all-videos-count">
            {safePrograms.length} program
            {safePrograms.length !== 1 ? "s" : ""} available
          </p>
        </div>
      </div>

      {safePrograms.length > 0 ? (
        <div className="training-page-programs-carousel">
          {safePrograms.map((program, idx) => (
            <div
              key={program.id}
              className="training-page-program-card"
              onClick={() =>
                navigate(`/programs/${program.id}`, {
                  state: {
                    program,
                    fromTraining: true,
                    trainingTab: "All Programs",
                  },
                })
              }
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`/programs/${program.id}`, {
                    state: {
                      program,
                      fromTraining: true,
                      trainingTab: "All Programs",
                    },
                  });
                }
              }}
            >
              <div className="training-page-program-card-thumb">
                {program.cover_image ? (
                  <img
                    src={program.cover_image}
                    alt={program.program_name}
                    className="training-page-program-card-image"
                  />
                ) : (
                  <div
                    className={`training-page-program-card-placeholder training-page-program-card-placeholder-${idx % 4}`}
                  >
                    <IconSchool size={34} color="rgba(255,255,255,0.9)" />
                  </div>
                )}
              </div>
              <div className="training-page-program-card-content">
                <h3 className="training-page-program-card-title">
                  {program.program_name}
                </h3>
                <p className="training-page-program-card-meta">
                  {program.completed_tasks || 0} of {program.total_tasks || 0}{" "}
                  tasks completed
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="training-page-programs-empty">
          No programs available.
        </div>
      )}
    </div>
  );
}

export default TrainingTabAllProgramsPage;
